/**
 * Командный «Журнал действий» (team-portal): лента ключевых действий по системе — кто (автор),
 * что (действие), когда (время), с привязкой к объекту/владельцу. Только чтение (записи не правятся
 * и не удаляются). Фильтры: клиент (uuid) и заказ (uuid).
 */
import { useEffect, useState } from 'react'
import { ScrollText } from 'lucide-react'
import { teamApi, type AuditDTO, type ClientSummary } from '@/lib/api'
import { useSeesAllClients } from '@/team/roles'
import { getErrorMessage } from '@/lib/errors'
import { auditActionLabel, auditDetailsText, auditObjectLabel } from '@/lib/orders'
import { PAGE_SIZE, pageCount } from '@/lib/pagination'
import { Pagination } from '@/design-system/components/data/pagination'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { Hint } from '@/design-system/components/primitives/text'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'

const ALL = 'all'

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
}

export function AuditPage() {
  const seesAll = useSeesAllClients()
  const [items, setItems] = useState<AuditDTO[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [clients, setClients] = useState<ClientSummary[]>([])
  const [client, setClient] = useState(ALL)
  const [order, setOrder] = useState('')
  const [debouncedOrder, setDebouncedOrder] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    teamApi
      .listClients(undefined, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setClients(res.items)
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [])

  // Изменение фильтра по заказу открывает первую страницу журнала (эталон `/team/orders`).
  // Пустое поле применяется сразу: иначе отложенный сброс срабатывал и на открытии страницы
  // и через 300 мс возвращал на первую страницу, если успели нажать «Вперёд».
  useEffect(() => {
    const next = order.trim()
    if (next === '') {
      setDebouncedOrder('')
      setPage(1)
      return
    }
    const t = setTimeout(() => {
      setDebouncedOrder(next)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [order])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    teamApi
      .listAudit(
        {
          client: client === ALL ? undefined : client,
          order: debouncedOrder || undefined,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        },
        controller.signal,
      )
      .then((res) => {
        if (controller.signal.aborted) return
        // Страница «уехала» за пределы результатов (фильтр сузил ленту) — на первую.
        if (res.items.length === 0 && page > 1) {
          setPage(1)
          return
        }
        setItems(res.items)
        setTotal(res.total)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить журнал'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [client, debouncedOrder, page])

  return (
    <div>
      <PageHead
        title="Журнал действий"
        subtitle={
          seesAll
            ? 'Лента ключевых действий по системе (только чтение)'
            : 'Действия по вашим клиентам (только чтение)'
        }
      />

      <Toolbar aria-label="Фильтры журнала">
        <Select
          value={client}
          onValueChange={(v) => {
            setClient(v)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-56" aria-label="Клиент">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Все клиенты</SelectItem>
            {clients.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="min-w-48 flex-1">
          <Input
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            placeholder="Фильтр по ID заказа"
            aria-label="Фильтр по идентификатору заказа"
          />
        </div>
      </Toolbar>

      <Panel>
        {error ? (
          <Alert variant="danger" className="m-4">
            {error}
          </Alert>
        ) : loading ? (
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        ) : items.length === 0 ? (
          <EmptyState icon={ScrollText} title="Записей нет" description="По выбранным фильтрам пусто" />
        ) : (
          <DataTable>
            <DataTableHead>
              <DataTableRow>
                <DataTableHeaderCell>Когда</DataTableHeaderCell>
                <DataTableHeaderCell>Кто</DataTableHeaderCell>
                <DataTableHeaderCell>Действие</DataTableHeaderCell>
                <DataTableHeaderCell>Объект</DataTableHeaderCell>
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {items.map((a) => {
                const detailsText = auditDetailsText(a.action, a.details)
                return (
                  <DataTableRow key={a.id}>
                    <DataTableCell className="whitespace-nowrap text-txt-secondary">
                      {formatDateTime(a.createdAt)}
                    </DataTableCell>
                    <DataTableCell className="font-medium">{a.actorName}</DataTableCell>
                    <DataTableCell>{auditActionLabel(a.action)}</DataTableCell>
                    <DataTableCell className="text-txt-secondary">
                      {auditObjectLabel(a)}
                      {detailsText ? ` · ${detailsText}` : ''}
                    </DataTableCell>
                  </DataTableRow>
                )
              })}
            </DataTableBody>
          </DataTable>
        )}
      </Panel>
      <Pagination page={page} totalPages={pageCount(total)} onChange={setPage} />
      <Hint className="mt-2.5">Журнал доступен только для чтения.</Hint>
    </div>
  )
}
